import { Button, Divider, Image, VStack } from '@chakra-ui/react';
import config from '@common/config';
import { useAutoInvestmentsModel } from '@common/models/AutoInvestmentsModel';
import { useBankAccountsModel } from '@common/models/BankAccountsModel';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import { useMyProfileModel } from '@common/models/MyProfileModel';
import { usePricingPlanModel } from '@common/models/PricingPlanModel';
import currency from '@common/utility/currency';
import { FrequencyMap } from '@services/appsync/mappings';
import { AutoInvestScheduleV2, BankDetailStatus, RegulatoryRuleType } from '@services/appsync/types';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import SplitView from 'src/apps/invest/common/shells/SplitView';
import { ROUTES } from 'src/apps/invest/config';
import { PAGES } from '../../constants';
import { useCheckoutCashflowViewModel } from '../../models/CheckoutCashflowViewModel';
import Confirm from '../components/Confirm';
import { Header } from '../components/Header';
import { Navigation } from '../components/Navigation';
import { Section } from '../components/Section';
import Summary from '../components/Summary';
import { CheckoutCashflowLoadingPage } from './CheckoutCashflowLoadingPage';

export const CheckoutCashflowConfirmationPage: React.FC = () => {
  const [isLoading, setIsloading] = useState(true);
  const viewModel = useCheckoutCashflowViewModel();
  const bankAccountsModel = useBankAccountsModel();
  const autoInvestmentModel = useAutoInvestmentsModel();

  const form = useForm<{ confirmed: boolean; signature: string }>();
  const hasABankCard = bankAccountsModel.bankAccounts.some(
    (account) => account.default && account.status === BankDetailStatus.VERIFIED,
  );
  const signatureWatch = useWatch({ control: form.control, name: 'signature' });
  const myProfile = useMyProfileModel();
  const investor = useInvestorAccountModel();
  const hasSignature = myProfile.storedSignatureUrl || (signatureWatch && signatureWatch.length > 0);
  const hasConfirmed = investor.isAccreditedInvestor() ? true : viewModel.confirmAgreement;
  const bankAccount = bankAccountsModel.getDefaultBankAccount();
  const pricingPlanModel = usePricingPlanModel();
  const documents = investor.subscriptionOffer?.documents?.filter((doc) => doc.show_on_checkout) ?? [];
  const [existingAutoInvest, setExistingAutoInvest] = useState<AutoInvestScheduleV2 | null>(null);

  const init = async () => {
    if (viewModel.existingAutoInvest) {
      const existing = (await autoInvestmentModel.getAutoInvest(viewModel.existingAutoInvest)).data;
      if (existing) {
        setExistingAutoInvest(existing);
      }
    }
  };

  useEffect(() => {
    init();
    setIsloading(false);
  }, []);

  documents.unshift({
    __typename: 'SubscriptionOfferDocument',
    title: 'Subscription Agreement',
    url: investor?.subscriptionOffer?.subscription_doc?.download_url ?? '',
  });

  const handleFormSubmission = form.handleSubmit(async () => {
    setIsloading(true);
    await viewModel.invest();
  });

  const handleSignatureSave = (dataUrl: string, blob: Blob) => {
    form.setValue('signature', dataUrl);
    viewModel.setNewSignatureDataUrl(dataUrl);
    viewModel.setNewSignatureBlob(blob);
  };

  const handleSignatureClear = () => {
    form.setValue('signature', '');
    viewModel.setNewSignatureDataUrl(undefined);
    viewModel.setNewSignatureBlob(undefined);
  };

  const handleAgreementChange = (e: ChangeEvent<HTMLInputElement>) => {
    form.setValue('confirmed', e.target.checked);
    viewModel.setConfirmAgreement(e.target.checked);
  };

  const isRegD = investor.subscriptionOffer?.regulatory_rule_type === RegulatoryRuleType.REG_D_506C;

  if (isLoading) return <CheckoutCashflowLoadingPage />;

  return (
    <SplitView.UI columnRatio={60}>
      <SplitView.LeftColumn>
        <Confirm.Form onSubmit={handleFormSubmission}>
          <Header
            title="Invest in Cash Flow"
            breadCrumb={
              viewModel.pricingPlan ? (
                <Navigation
                  links={[
                    { label: 'Amount', onClick: () => viewModel.setPage(PAGES.amount) },
                    { label: 'Pricing Plan', onClick: () => viewModel.setPage(PAGES.pricingPlan) },
                    { isCurrent: true, label: 'Confirm' },
                  ]}
                />
              ) : (
                <Navigation
                  links={[
                    { label: 'Amount', onClick: () => viewModel.setPage(PAGES.amount) },
                    { isCurrent: true, label: 'Confirm' },
                  ]}
                />
              )
            }
          />
          {documents.length > 0 && (
            <Section icon="file" title="Review Documents" description="Please review the documents listed.">
              <Confirm.Documents documents={documents.map((doc) => [doc.title, doc.url])} />
            </Section>
          )}
          <Divider />
          {myProfile.storedSignatureUrl ? (
            <Section icon="signature" title="Stored Signature">
              <Image
                maxW={['100%', 250, 250, 300]}
                src={myProfile.storedSignatureUrl}
                alt={`${investor.investorAccount?.individual_details?.first_name} ${investor.investorAccount?.individual_details?.last_name}`}
              />
              <Confirm.Agreement onChange={handleAgreementChange} />
            </Section>
          ) : (
            <Section icon="signature" title="Sign Documents" description={`Please sign below:`}>
              <Confirm.Signature onSave={handleSignatureSave} onClear={handleSignatureClear} />
              <Confirm.Agreement onChange={handleAgreementChange} />
            </Section>
          )}
        </Confirm.Form>
      </SplitView.LeftColumn>
      <SplitView.RightColumn>
        <VStack position={['static', 'static', 'static', 'sticky']} top="0px" gap={'31px'} width="100%">
          {existingAutoInvest ? (
            <>
              <Summary.Container label="Existing Auto Invest">
                <Summary.Item
                  label={isRegD ? 'Cash Flow (Accredited)' : 'Cash Flow'}
                  variant={'highlight'}
                  image="cash-flow-icon"
                  value={currency.format(existingAutoInvest.amount)}
                />
                <Summary.Item
                  label="Frequency"
                  value={autoInvestmentModel.getFrequencyDescription({
                    dayOfWeek:
                      existingAutoInvest.days_of_week && existingAutoInvest.days_of_week?.length > 0
                        ? existingAutoInvest.days_of_week[0]
                        : null,
                    daysOfMonth: existingAutoInvest.days_of_month ?? null,
                    frequency: existingAutoInvest.frequency as FrequencyMap,
                  })}
                />
              </Summary.Container>
              <Summary.Container label="Updated Auto Invest">
                <Summary.Item
                  label={isRegD ? 'Cash Flow (Accredited)' : 'Cash Flow'}
                  variant={'highlight'}
                  image="cash-flow-icon"
                  value={currency.format(viewModel.amount)}
                />
                <Summary.Item label="Frequency" value={viewModel.getFrequencyDescription()} />
                {bankAccount?.name && (
                  <Summary.Item
                    label="Account"
                    value={`${bankAccount?.name} - ${bankAccount?.account_type} (...${
                      bankAccount?.account_number || ''
                    })`}
                  />
                )}
                {viewModel.pricingPlan && (
                  <Summary.Item
                    label="Pricing Plan"
                    value={pricingPlanModel.getFriendlyName(viewModel.pricingPlan)}
                    description={`Applies to accounts below $${config.MIN_ACCOUNT_BALANCE_FEE_THRESHOLD}`}
                  />
                )}
              </Summary.Container>
            </>
          ) : (
            <Summary.Container label="Purchase Summary">
              <Summary.Item
                label={isRegD ? 'Cash Flow (Accredited)' : 'Cash Flow'}
                variant={'highlight'}
                image="cash-flow-icon"
                value={currency.format(viewModel.amount)}
              />
              <Summary.Item label="Frequency" value={viewModel.getFrequencyDescription()} />
              <Summary.Item
                label="Account"
                value={`${bankAccount?.name} - ${bankAccount?.account_type} (...${bankAccount?.account_number || ''})`}
              />
              {viewModel.pricingPlan && (
                <Summary.Item
                  label="Pricing Plan"
                  value={pricingPlanModel.getFriendlyName(viewModel.pricingPlan)}
                  description={`Applies to accounts below $${config.MIN_ACCOUNT_BALANCE_FEE_THRESHOLD}`}
                />
              )}
            </Summary.Container>
          )}
          <Confirm.Form onSubmit={handleFormSubmission}>
            <Confirm.SubmitButton isDisabled={!hasABankCard || !hasSignature || !hasConfirmed || isLoading} />
            <Button as={Link} to={ROUTES.dashboard} variant="link" color="grey">
              Cancel Investment
            </Button>
          </Confirm.Form>
        </VStack>
      </SplitView.RightColumn>
    </SplitView.UI>
  );
};
